/**
 * custom.js
 *
 * Entry point for all custom JavaScript in this theme. Loaded as a module from
 * snippets/scripts.liquid, and registered in the importmap as `@theme/custom`
 * so other modules can `import { ... } from '@theme/custom'`.
 *
 * Conventions to follow when adding code here:
 *
 * - Custom elements extend `Component` from `@theme/component`, which gives you
 *   `this.refs.<name>` for descendants marked `ref="<name>"`, and declarative
 *   handlers via `on:click="/methodName"` attributes in Liquid. Prefer those
 *   over manual `querySelector` + `addEventListener`.
 * - Register elements as `<something-component>` and guard with
 *   `customElements.get()` so a double load can never throw.
 * - Implement `updatedCallback()` (not just `connectedCallback()`) when state
 *   has to survive a Section Rendering API re-render — morph patches the DOM in
 *   place rather than replacing it.
 * - Listen for cart changes with `StandardEvents.cartLinesUpdate` from
 *   `@shopify/events`; the theme does not own the cart AJAX layer.
 * - Anything that only affects presentation belongs in assets/custom.css.
 *
 * Current sections that rely on this file:
 *   sections/segment-with-products.liquid  no JS needed (CSS grid + scroll snap)
 *   sections/banner-slider.liquid          no JS needed (reuses <slideshow-component>)
 */

/**
 * Registers a custom element once, tolerating a repeated module evaluation.
 *
 * @param {string} tagName - The tag name to register, e.g. 'my-component'.
 * @param {CustomElementConstructor} constructor - The element class.
 */
export function defineOnce(tagName, constructor) {
  if (customElements.get(tagName)) return;

  customElements.define(tagName, constructor);
}

/**
 * Banner slider autoplay: Horizon suspends on mouseenter and skips ticks on
 * :hover. A full-bleed hero is almost always hovered, so autoslide dies.
 * Keep rolling; only stop when explicitly paused or the tab is hidden.
 */
(function patchBannerSliderAutoplay() {
  const isBanner = (el) =>
    el instanceof HTMLElement
    && (el.classList.contains('banner-slider__slideshow') || Boolean(el.closest('.banner-slider')));

  const clearBannerInterval = (el) => {
    if (!el.__bannerAutoplayInterval) return;
    clearInterval(el.__bannerAutoplayInterval);
    el.__bannerAutoplayInterval = undefined;
  };

  const apply = () => {
    const Slideshow = customElements.get('slideshow-component');
    if (!Slideshow?.prototype || Slideshow.prototype.__bannerAutoplayPatched) return;

    const proto = Slideshow.prototype;
    const originalPlay = proto.play;
    const originalPause = proto.pause;
    const originalSuspend = proto.suspend;
    const originalResume = proto.resume;

    proto.play = function play(interval = this.autoplayInterval) {
      if (!isBanner(this)) return originalPlay.call(this, interval);

      const ms = interval ?? this.autoplayInterval;
      if (!ms || this.__bannerAutoplayInterval) return;

      this.paused = false;
      this.__bannerAutoplayInterval = setInterval(() => {
        if (document.hidden) return;
        this.next();
      }, ms);
    };

    proto.pause = function pause() {
      if (!isBanner(this)) return originalPause.call(this);
      clearBannerInterval(this);
      this.paused = true;
      originalSuspend.call(this);
    };

    // Ignore hover suspend — visibility is handled inside the interval tick.
    proto.suspend = function suspend() {
      if (isBanner(this)) return;
      return originalSuspend.call(this);
    };

    proto.resume = function resume() {
      if (!isBanner(this)) return originalResume.call(this);
      if (!this.autoplay || this.paused) return;
      clearBannerInterval(this);
      originalSuspend.call(this);
      this.play();
    };

    proto.__bannerAutoplayPatched = true;

    document.querySelectorAll('.banner-slider slideshow-component').forEach((el) => {
      originalSuspend.call(el);
      clearBannerInterval(el);
      if (el.autoplay) {
        el.paused = false;
        el.play();
      }
    });
  };

  if (customElements.get('slideshow-component')) {
    apply();
  } else {
    customElements.whenDefined('slideshow-component').then(apply);
  }
})();

/*
 * Register custom components below.
 *
 * Example:
 *
 *   import { Component } from '@theme/component';
 *
 *   class ReadingProgress extends Component {
 *     requiredRefs = ['bar'];
 *
 *     connectedCallback() {
 *       super.connectedCallback();
 *       this.#update();
 *       document.addEventListener('scroll', this.#update, { passive: true });
 *     }
 *
 *     disconnectedCallback() {
 *       super.disconnectedCallback();
 *       document.removeEventListener('scroll', this.#update);
 *     }
 *
 *     #update = () => {
 *       const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
 *       const progress = scrollTop / Math.max(1, scrollHeight - clientHeight);
 *       this.refs.bar.style.setProperty('--progress', String(progress));
 *     };
 *   }
 *
 *   defineOnce('reading-progress-component', ReadingProgress);
 */

class CustomWiderrufForm extends HTMLElement {
  connectedCallback() {
    this.form = this.closest('form');
    this.email = this.querySelector('[data-widerruf-email]');
    this.body = this.querySelector('[data-widerruf-body]');
    if (!this.form) return;
    this.form.addEventListener('submit', this);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this);
  }

  handleEvent(event) {
    if (event.type !== 'submit') return;
    const order = this.form.querySelector('[name="contact[Bestellnummer]"]')?.value ?? '';
    const first = this.form.querySelector('[name="contact[Vorname]"]')?.value ?? '';
    const last = this.form.querySelector('[name="contact[Name]"]')?.value ?? '';
    if (this.body) {
      this.body.value = [
        'Widerruf',
        `Bestellnummer: ${order}`,
        `E-Mail: ${this.email?.value ?? ''}`,
        `Name: ${first} ${last}`.trim(),
      ].join('\n');
    }
  }
}

defineOnce('custom-widerruf-form', CustomWiderrufForm);

/** Live SIC icon pool from buchinger-wilhelmi-shop.com (3 random each challenge). */
const PARTNER_CAPTCHA_ICONS = [
  {
    id: 'uhr',
    label: 'Uhr',
    paths: [
      'M128 223.5c-51 0-92.5-41.5-92.5-92.5S77 38.5 128 38.5 220.5 80 220.5 131 179 223.5 128 223.5zm0-176C82 47.5 44.5 85 44.5 131S82 214.5 128 214.5s83.5-37.5 83.5-83.5S174 47.5 128 47.5z',
      'M184 135.5h-56c-2.5 0-4.5-2-4.5-4.5V75c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5v51.5H184c2.5 0 4.5 2 4.5 4.5s-2 4.5-4.5 4.5z',
    ],
  },
  {
    id: 'hand',
    label: 'Hand',
    paths: [
      'M207.5 50.5c-5.5 0-10.4 2.4-14.3 6.2v-8c0-13.7-9.7-24.7-21.7-24.7-5.9 0-11.2 2.7-15.1 7.1-2.4-10.6-10.8-18.5-20.9-18.5-9.2 0-17 6.5-20.1 15.7-4-5.2-9.7-8.5-16.3-8.5-12 0-21.7 11.1-21.7 24.7v96.9l-10.2-18.6c-6-11.8-19.3-15.9-29.6-9-10.4 6.8-13.9 22-7.9 33.8L34 156c15.6 31.5 26 52.3 37.7 66 12.3 14.4 25.5 20.3 45.5 20.3 15.7 0 54.7 4.8 73.2-4.6 30.7-15.6 38.8-52.1 38.8-82V75.2c0-13.6-9.7-24.7-21.7-24.7zm14.4 94.8v10.4c0 26.7-6.1 60.5-34.6 74.7-17.6 8.8-55.7 3.7-70.2 3.7-36.1 0-46.4-20.7-76.9-82.2l-4.2-8.5c-3.9-7.9-1.4-18 5.5-22.4 6.8-4.4 15.5-1.7 19.5 6 0 0 0 .1.1.1L78 157.9c1.1 1.9 3.3 2.5 5 1.3 1.1-.8 1.7-2.1 1.7-3.5V44.4c0-9.1 6.5-16.5 14.4-16.5s14.4 7.4 14.4 16.5v70.1c0 2.3 1.6 4.1 3.6 4.1s3.6-1.8 3.6-4.1V54.6l.3-17.4c0-9.1 6.5-16.5 14.4-16.5 8 0 14.4 7.4 14.4 16.5v10.6c0 .3-.1.6-.1.9v55.7l.1-.3v3.2c0 2.3 1.6 4.1 3.6 4.1s3.6-1.8 3.6-4.1V47.8c.4-8.7 6.6-15.6 14.4-15.6 8 0 14.4 7.4 14.4 16.5V73c-.1.7-.2 1.4-.2 2.2v35l.2-.1v8.7c0 2.3 1.6 4.1 3.6 4.1s3.6-1.8 3.6-4.1V73c.9-8.1 6.9-14.3 14.3-14.3 8 0 14.4 7.4 14.4 16.5v70.1z',
    ],
  },
  {
    id: 'reagenzglas',
    label: 'Reagenzglas',
    paths: [
      'M214 216.6c-.7 1.3-2.1 2.1-3.6 2.1H45.5c-2.3 0-4.1-1.8-4.1-4.1 0-.8.2-1.5.6-2.1l30.4-50.7c16-2.5 34.1 1.2 53.7 11.2 18.9 9.6 34.9 12.8 47.7 12.8 7.5.1 14.9-1.2 22-3.6l18.2 30.3c.8 1.2.8 2.8 0 4.1M106.8 104.5c.4-.6.6-1.4.6-2.1V37.3h41.2v65.1c0 .7.2 1.5.6 2.1l42.3 70.4c-13.2 4.1-33.5 4.8-61.6-9.4-18.6-9.4-36.1-13.6-52.1-12.7l29-48.3zM221 208.2l-64.2-107V37.3h12.4c2.3 0 4.1-1.8 4.1-4.1s-1.8-4.1-4.1-4.1H86.8c-2.3 0-4.1 1.8-4.1 4.1s1.8 4.1 4.1 4.1h12.4v63.9L35 208.2c-3.5 5.9-1.6 13.5 4.2 17 1.9 1.2 4.1 1.8 6.3 1.8h164.9c6.8 0 12.4-5.5 12.4-12.4 0-2.3-.6-4.5-1.8-6.4z',
    ],
  },
  {
    id: 'badewanne',
    label: 'Badewanne',
    paths: [
      'M72 220.5c-2.5 0-4.5-2-4.5-4.5v-24c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5v24c0 2.5-2 4.5-4.5 4.5zM184 220.5c-2.5 0-4.5-2-4.5-4.5v-24c0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5v24c0 2.5-2 4.5-4.5 4.5zM200 148.5h-64c-2.5 0-4.5-2-4.5-4.5V96c0-2.5 2-4.5 4.5-4.5h64c2.5 0 4.5 2 4.5 4.5v48c0 2.5-2 4.5-4.5 4.5zm-59.5-9h55v-39h-55v39z',
      'M192 196.5H64c-28.9 0-52.5-23.6-52.5-52.5v-32c0-6.9 5.6-12.5 12.5-12.5h112c2.5 0 4.5 2 4.5 4.5s-2 4.5-4.5 4.5H24c-1.9 0-3.5 1.6-3.5 3.5v32c0 24 19.5 43.5 43.5 43.5h128c24 0 43.5-19.5 43.5-43.5v-32c0-1.9-1.6-3.5-3.5-3.5h-32c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5h32c6.9 0 12.5 5.6 12.5 12.5v32c0 28.9-23.6 52.5-52.5 52.5z',
      'M56 108.5c-2.5 0-4.5-2-4.5-4.5V52c0-13.5 11-24.5 24.5-24.5h.1c11.6 0 21.8 8.2 24.3 19.5.5 2.4-1 4.8-3.4 5.4-2.4.5-4.8-1-5.4-3.4C90 41.7 83.5 36.5 76 36.5c-8.6 0-15.5 7-15.5 15.5v52c0 2.5-2 4.5-4.5 4.5z',
    ],
  },
  {
    id: 'tasche',
    label: 'Tasche',
    paths: [
      'M216 212.5H40c-6.9 0-12.5-5.6-12.5-12.5V72c0-6.9 5.6-12.5 12.5-12.5h176c6.9 0 12.5 5.6 12.5 12.5v128c0 6.9-5.6 12.5-12.5 12.5zM40 68.5c-1.9 0-3.5 1.6-3.5 3.5v128c0 1.9 1.6 3.5 3.5 3.5h176c1.9 0 3.5-1.6 3.5-3.5V72c0-1.9-1.6-3.5-3.5-3.5H40z',
      'M168 68.5c-2.5 0-4.5-2-4.5-4.5V48c0-6.3-5.2-11.5-11.5-11.5h-48c-6.3 0-11.5 5.2-11.5 11.5v16c0 2.5-2 4.5-4.5 4.5s-4.5-2-4.5-4.5V48c0-11.3 9.2-20.5 20.5-20.5h48c11.3 0 20.5 9.2 20.5 20.5v16c0 2.5-2 4.5-4.5 4.5zM127.7 148.5c-34.3 0-68.2-9.1-97.9-26.3-2.2-1.2-2.9-4-1.6-6.1 1.2-2.2 4-2.9 6.1-1.6 28.4 16.4 60.7 25.1 93.4 25.1h.6c32.8 0 65.1-8.7 93.5-25.1 2.2-1.2 4.9-.5 6.1 1.6 1.2 2.2.5 4.9-1.6 6.1-29.7 17.2-63.6 26.3-98 26.3h-.6z',
      'M144 116.5h-32c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5h32c2.5 0 4.5 2 4.5 4.5s-2 4.5-4.5 4.5z',
    ],
  },
  {
    id: 'tropfen',
    label: 'Tropfen',
    paths: [
      'M173.6 156.7c-3.5 19.4-18.6 34.5-38 38h-.6c-1.9 0-3.5-1.6-3.5-3.5 0-1.7 1.2-3.1 2.9-3.4 16-2.7 29.5-16.3 32.2-32.3.4-1.9 2.3-3.1 4.2-2.7 1.8.4 3 2.1 2.8 3.9M128 215.8c-36.8 0-66.7-29.9-66.7-66.7 0-31.5 18.6-59.5 34.1-77.5 9.8-11.2 20.7-21.5 32.6-30.5 11.9 9.1 22.8 19.3 32.6 30.6 15.6 17.9 34.1 45.9 34.1 77.4 0 36.8-29.9 66.7-66.7 66.7m37.8-148.9c-10.7-12.3-22.7-23.4-35.8-33.1-1.2-.8-2.8-.8-4 0-13.1 9.7-25.1 20.7-35.7 33.1-23.6 27.1-36 55.5-36 82.2 0 40.7 33 73.8 73.8 73.8s73.8-33 73.8-73.8c-.1-26.7-12.6-55.2-36.1-82.2',
    ],
  },
  {
    id: 'besteck',
    label: 'Besteck',
    paths: [
      'M119.8 32.5c-.4-2.2-2.5-3.8-4.8-3.4-2.2.4-3.8 2.5-3.4 4.8l8.2 49.1c0 20.5-16.6 37.1-37.1 37.1S45.6 103.5 45.6 83l8.2-49.1c.4-2.2-1.1-4.4-3.4-4.8-2.2-.4-4.4 1.1-4.8 3.4L37.5 82c-.1.2-.2.5-.2.7 0 23.4 17.9 43 41.2 45.2v95c0 2.3 1.8 4.1 4.1 4.1s4.1-1.8 4.1-4.1v-95c23.3-2.2 41.2-21.7 41.2-45.2V82l-8.1-49.5zm90.7 7.3c-43 24.6-48.6 102.3-49.4 121.1h49.4V39.8zm8.2-6.6v189.6c0 2.3-1.8 4.1-4.1 4.1s-4.1-1.8-4.1-4.1v-53.6h-53.6c-2.3 0-4.1-1.8-4.1-4.1.4-19.4 2.8-38.8 7.3-57.7 9.8-40.4 28-67.4 52.9-78.1 2.1-.9 4.5.1 5.4 2.2.2.6.3 1.1.3 1.7M78.5 82.7V33.2c0-2.3 1.8-4.1 4.1-4.1s4.1 1.8 4.1 4.1v49.5c0 2.3-1.8 4.1-4.1 4.1-2.2 0-4.1-1.9-4.1-4.1',
    ],
  },
  {
    id: 'lineal',
    label: 'Lineal',
    paths: [
      'M229.9 86.3 86.3 229.9c-1.6 1.6-4.2 1.6-5.8 0l-54.3-54.3c-1.6-1.6-1.6-4.2 0-5.8l35.8-36 30.1 30.1c1.6 1.6 4.2 1.6 5.8 0 1.6-1.6 1.6-4.2 0-5.8l-30-30.1L95 100.9l30.1 30.1c1.6 1.6 4.2 1.6 5.8 0s1.6-4.2 0-5.8L100.8 95 128 67.9 158.1 98c1.6 1.6 4.2 1.6 5.8 0 1.6-1.6 1.6-4.2 0-5.8L133.8 62l35.9-35.9c1.6-1.6 4.2-1.6 5.8 0l54.3 54.3c1.7 1.7 1.7 4.3.1 5.9m5.8-11.7-54.3-54.3c-4.8-4.8-12.7-4.9-17.5 0L20.3 163.9c-4.8 4.8-4.9 12.7 0 17.5l54.3 54.3c4.8 4.8 12.7 4.9 17.5 0L235.7 92.1c4.8-4.8 4.8-12.7 0-17.5',
    ],
  },
  {
    id: 'kalender',
    label: 'Kalender',
    paths: [
      'm169.2 156.9-24.7 33h24.7c2.3 0 4.1 1.8 4.1 4.1s-1.8 4.1-4.1 4.1h-33c-2.3 0-4.1-1.8-4.1-4.1 0-.9.3-1.8.8-2.5l29.7-39.5c1.6-2.1 2.5-4.8 2.5-7.5 0-6.8-5.5-12.4-12.4-12.4-4.4 0-8.5 2.4-10.7 6.2-1.2 1.9-3.8 2.5-5.7 1.3-1.8-1.2-2.4-3.5-1.4-5.4 5.7-9.9 18.3-13.2 28.2-7.5 6.4 3.7 10.3 10.5 10.3 17.9 0 4.4-1.5 8.7-4.2 12.3M107.4 128v66c0 2.3-1.8 4.1-4.1 4.1s-4.1-1.8-4.1-4.1v-59.3L88.7 140c-2 1-4.5.2-5.5-1.8s-.2-4.5 1.8-5.5l16.5-8.2c2-1 4.5-.2 5.5 1.8.2.4.4 1.1.4 1.7m103.1 94.8h-165c-2.3 0-4.1-1.8-4.1-4.1V99.1h173.2v119.6c0 2.3-1.9 4.1-4.1 4.1M45.5 49.7h28.9V62c0 2.3 1.8 4.1 4.1 4.1s4.1-1.8 4.1-4.1V49.7h90.7V62c0 2.3 1.8 4.1 4.1 4.1s4.1-1.8 4.1-4.1V49.7h28.9c2.3 0 4.1 1.8 4.1 4.1v37.1H41.4V53.8c0-2.3 1.9-4.1 4.1-4.1m165-8.3h-28.9V29.1c0-2.3-1.8-4.1-4.1-4.1s-4.1 1.8-4.1 4.1v12.4H82.7V29.1c0-2.3-1.8-4.1-4.1-4.1s-4.1 1.8-4.1 4.1v12.4h-29c-6.8 0-12.4 5.5-12.4 12.4v164.9c0 6.8 5.5 12.4 12.4 12.4h164.9c6.8 0 12.4-5.5 12.4-12.4v-165c0-6.8-5.5-12.4-12.3-12.4',
    ],
  },
  {
    id: 'apfel',
    label: 'Apfel',
    paths: [
      'M128 238.3c-16.6 0-32.1-3-45.9-9-12.9-5.5-20.1-11.8-22-13.6-24-22.5-38.2-54.4-39.1-87.3-.5-19.3 6.9-38.1 20.3-51.6 13.1-13.3 30.6-20.4 49.2-20.2 14 .2 27.7 4.7 39.1 12.9 11.6-8.3 25.6-12.9 39.9-12.9h.2c24.5 0 43.3 9.4 54.4 27.3 10 16.1 11.1 34.2 11.1 41.3 0 47.1-27.3 80.1-35.7 89-6.1 6.3-31.8 24.1-71.5 24.1zM89.5 65.6c-15.8 0-30.7 6.2-41.8 17.5C36 95 29.6 111.4 30 128.2c.8 30.5 14 60 36.2 81 2.2 2.1 22.5 20.2 61.8 20.2 37.2 0 60.5-16.8 64.8-21.3 16.1-17.2 33.3-48.1 33.3-82.9 0-6.3-.9-22.4-9.7-36.5-9.5-15.3-25.3-23-46.8-23h-.1c-13.4 0-26.6 4.6-37 13-1.6 1.3-4 1.3-5.6 0-10.3-8.2-23.2-12.9-36.4-13-.5 0-.7-.1-1-.1zM137.5 46.7c-.4 0-.8 0-1.1-.1-2.4-.6-3.9-3.1-3.2-5.5 4.2-16.1 18.7-27.4 35.3-27.4h1c2.5 0 4.5 2 4.5 4.5s-2 4.5-4.5 4.5h-1c-12.5 0-23.5 8.5-26.6 20.6-.6 2-2.4 3.4-4.4 3.4z',
    ],
  },
  {
    id: 'puls',
    label: 'Puls',
    paths: [
      'M179.5 33.2c-22.1 0-41.1 10.5-51.5 27.9-10.4-17.4-29.4-27.9-51.5-27.9-33 0-59.7 26.8-59.8 59.8v2.2c.1 2.3 2 4.1 4.3 4s4.1-2 4-4.3V93c0-28.5 23-51.5 51.5-51.6 21.8 0 40 11.7 47.7 30.4.9 2.1 3.3 3.1 5.4 2.2 1-.4 1.8-1.2 2.2-2.2 7.7-18.8 26-30.4 47.7-30.4 28.4 0 51.5 23.1 51.5 51.5 0 59.8-88.6 112.8-103.1 121-8.7-5-44.8-26.4-71.8-55.8-1.6-1.6-4.2-1.7-5.8-.1-1.6 1.5-1.7 4-.2 5.7 31.7 34.5 74.1 57.6 75.9 58.6 1.2.7 2.7.7 3.9 0 19.3-10.8 37.5-23.5 54.3-37.9 36.5-31.2 55-62 55-91.5.1-32.9-26.7-59.7-59.7-59.7M70.3 132.1H29.1c-2.3 0-4.1-1.8-4.1-4.1s1.8-4.1 4.1-4.1h39L83.3 101c1.3-1.9 3.8-2.4 5.7-1.1.5.3.8.7 1.1 1.1l29.5 44.3 13.1-19.6c.8-1.1 2.1-1.8 3.4-1.8H161c2.3 0 4.1 1.8 4.1 4.1s-1.8 4.1-4.1 4.1h-22.5L123.2 155c-1.3 1.9-3.8 2.4-5.7 1.1-.5-.3-.8-.7-1.1-1.1l-29.5-44.3-13.1 19.6c-.8 1.1-2.1 1.8-3.5 1.8',
    ],
  },
];

class CustomPartnerForm extends HTMLElement {
  #answer = '';

  connectedCallback() {
    this.form = this.closest('form');
    this.body = this.querySelector('[data-partner-body]');
    this.prompt = this.querySelector('[data-partner-captcha-prompt]');
    this.error = this.querySelector('[data-partner-captcha-error]');
    this.options = this.querySelector('[data-partner-captcha-options]');
    this.#shuffleCaptcha();
    if (!this.form) return;
    this.form.addEventListener('submit', this);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this);
  }

  #shuffle(items) {
    const list = [...items];
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  }

  #pickIcons(count) {
    return this.#shuffle(PARTNER_CAPTCHA_ICONS).slice(0, count);
  }

  #renderIcon(icon) {
    const paths = icon.paths.map((d) => `<path fill="currentColor" d="${d}"/>`).join('');
    return `<svg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg" focusable="false">${paths}</svg>`;
  }

  #shuffleCaptcha() {
    if (!this.options) return;

    const name = this.options.dataset.partnerCaptchaName || 'partner_captcha';
    const trio = this.#pickIcons(3);
    const answer = trio[Math.floor(Math.random() * trio.length)];
    this.#answer = answer.id;
    if (this.prompt) this.prompt.textContent = answer.label;

    this.options.replaceChildren();
    this.#shuffle(trio).forEach((icon) => {
      const label = document.createElement('label');
      label.className = 'custom-partner__captcha-option';
      label.innerHTML = `
        <input
          class="custom-partner__captcha-input"
          type="radio"
          name="${name}"
          value="${icon.id}"
          data-partner-captcha-option
          tabindex="-1"
        >
        <span class="custom-partner__captcha-icon" aria-hidden="true">${this.#renderIcon(icon)}</span>
        <span class="visually-hidden">${icon.label}</span>
      `;
      this.options.appendChild(label);
    });

    if (this.error) this.error.hidden = true;
  }

  handleEvent(event) {
    if (event.type !== 'submit') return;

    const selected = this.querySelector('[data-partner-captcha-option]:checked');
    if (!selected || selected.value !== this.#answer) {
      event.preventDefault();
      if (this.error) this.error.hidden = false;
      this.#shuffleCaptcha();
      return;
    }

    const last = this.querySelector('[data-partner-last]')?.value ?? '';
    const first = this.querySelector('[data-partner-first]')?.value ?? '';
    const company = this.querySelector('[data-partner-company]')?.value ?? '';
    const email = this.querySelector('[data-partner-email]')?.value ?? '';
    const phone = this.querySelector('[data-partner-phone]')?.value ?? '';
    const availability = this.querySelector('[data-partner-availability]')?.value ?? '';
    const website = this.querySelector('[data-partner-website]')?.value ?? '';
    const message = this.querySelector('[data-partner-message]')?.value ?? '';

    if (this.body) {
      this.body.value = [
        'Partnerprogramm Anfrage',
        `Name: ${last}`,
        `Vorname: ${first}`,
        `Firma: ${company}`,
        `E-Mail: ${email}`,
        `Telefon: ${phone}`,
        `Erreichbarkeit: ${availability}`,
        `Website: ${website}`,
        '',
        'Nachricht:',
        message,
      ].join('\n');
    }
  }
}

defineOnce('custom-partner-form', CustomPartnerForm);
